import { TableRow, TableCell, IconButton, Typography } from "@mui/material"
import { Delete } from "@mui/icons-material"
import { useCallback, useMemo } from "react"
import classnames from "classnames"
import { factorRuleMaxValidator, factorRuleMinValidator } from "connected-components/optimize-experiments/form-validation-schemas/rules"
import { type OptimizeFormApi, useFormContext } from "connected-components/optimize-experiments/form-context"
import type { FactorRuleRowProps } from "../../types"
import { parseFieldValue } from "../../helpers"
import FactorRuleInput from "../input"
import { CELL_CONFIG } from "./cell-config"
import { FactorRuleTableCell } from "./table-cell"
import { getTableHeaders } from "../table"

export default function FactorRuleRow({
  rule,
  index,
  ruleDisplayType,
  handleUpdateRule,
  handleRemoveRule,
  hasBase,
  handleToggleActiveValue,
}: FactorRuleRowProps) {
  const form: OptimizeFormApi = useFormContext()
  const columnCount = getTableHeaders(ruleDisplayType, hasBase ?? false).length

  const visibleCells = useMemo(
    () => CELL_CONFIG.filter((cell) => hasBase || cell.id !== "active"),
    [hasBase],
  )

  const handleWeightCellClick = useCallback(() => {
    if (handleToggleActiveValue) {
      handleToggleActiveValue(rule, ruleDisplayType)
    }
  }, [handleToggleActiveValue, rule, ruleDisplayType])

  return (
    <form.Subscribe
      selector={(state) => ({
        minError: state.fieldMeta[`rules.${ruleDisplayType}[${index}].min`]?.errors?.[0],
        maxError: state.fieldMeta[`rules.${ruleDisplayType}[${index}].max`]?.errors?.[0],
      })}
    >
      {({ minError, maxError }) => {
        const minMaxValueError = minError || maxError
        return (
          <>
            <TableRow className={classnames("table-row", { "has-error": minMaxValueError })}>
              {visibleCells.map((cell) => {
                const isWeightCell = cell.id === "current" || cell.id === "active"
                const onClick = isWeightCell && hasBase && handleToggleActiveValue ? handleWeightCellClick : undefined

                return (
                  <FactorRuleTableCell
                    key={cell.id}
                    cell={cell}
                    rule={rule}
                    onClick={onClick}
                  />
                )
              })}

              <form.Field
                name={`rules.${ruleDisplayType}[${index}].min`}
                validators={{
                  onChangeListenTo: [`rules.${ruleDisplayType}[${index}].max`],
                  onChange: factorRuleMinValidator(ruleDisplayType, index),
                  onMount: factorRuleMinValidator(ruleDisplayType, index),
                }}
              >
                {(field) => (
                  <TableCell className="table-cell">
                    <FactorRuleInput
                      ruleId={rule.id}
                      field="min"
                      value={field.state.value?.toString() ?? ""}
                      onChange={(value: string | undefined) => {
                        field.handleChange(parseFieldValue(value))
                      }}
                      onBlur={(value: string | undefined) => {
                        field.handleBlur()
                        handleUpdateRule(rule, "min", value ?? "", ruleDisplayType)
                      }}
                    />
                  </TableCell>
                )}
              </form.Field>

              <form.Field
                name={`rules.${ruleDisplayType}[${index}].max`}
                validators={{
                  onChangeListenTo: [`rules.${ruleDisplayType}[${index}].min`],
                  onChange: factorRuleMaxValidator(ruleDisplayType, index),
                  onMount: factorRuleMaxValidator(ruleDisplayType, index),
                }}
              >
                {(field) => (
                  <TableCell className="table-cell">
                    <FactorRuleInput
                      ruleId={rule.id}
                      field="max"
                      value={field.state.value?.toString() ?? ""}
                      onChange={(value: string | undefined) => {
                        field.handleChange(parseFieldValue(value))
                      }}
                      onBlur={(value: string | undefined) => {
                        field.handleBlur()
                        handleUpdateRule(rule, "max", value ?? "", ruleDisplayType)
                      }}
                    />
                  </TableCell>
                )}
              </form.Field>

              <TableCell className="table-cell">
                <IconButton
                  aria-label="Delete"
                  onClick={() => handleRemoveRule(rule, ruleDisplayType)}
                  size="small"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
            {minMaxValueError && (
              <TableRow className="table-row-error">
                <TableCell colSpan={columnCount} className="table-cell">
                  <Typography variant="caption" color="error">
                    {minMaxValueError}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </>
        )
      }}
    </form.Subscribe>
  )
}
